import { useRef, useEffect, useState } from 'react';
import type { GameState, Card as CardType, ChatChannel } from '@flip7/shared';
import { calculateScore, CHAT_MAX_LENGTH } from '@flip7/shared';
import { CardGrid } from '../components/CardGrid';
import { CardReveal } from '../components/CardReveal';
import { DigitalModeControls } from '../components/DigitalModeControls';
import { PlayerStrip } from '../components/PlayerStrip';
import { ReactionBar } from '../components/ReactionBar';
import { ReactionToasts } from '../components/ReactionToasts';
import { SoundbiteSheet } from '../components/SoundbiteSheet';
import { useSound } from '../hooks/useSound';
import { getMuted, setMuted } from '../hooks/useSound';
import { formatRoundEvent, eventIcon } from '../utils/round-events';
import type { useReactions } from '../hooks/useReactions';
import type { useChat } from '../hooks/useChat';

interface GameBoardScreenProps {
  gameState: GameState;
  playerId: string | null;
  isHost: boolean;
  isSpectator?: boolean;
  onAddCard: (playerId: string, card: CardType) => void;
  onRemoveCard: (playerId: string, card: CardType) => void;
  onStay: (playerId: string) => void;
  onBust: (playerId: string) => void;
  onNextPlayer: () => void;
  onUndo: () => void;
  onEndRound: () => void;
  onHit: (playerId: string) => void;
  onChooseTarget: (targetPlayerId: string) => void;
  onSelectSwapCards?: (sourcePlayerId: string, sourceCardIndex: number, targetPlayerId: string, targetCardIndex: number) => void;
  onSelectStealTarget?: (targetPlayerId: string, cardIndex: number) => void;
  onSelectDiscardTarget?: (targetPlayerId: string, cardIndex: number) => void;
  onForfeitAction?: () => void;
  onKickPlayer?: (playerId: string) => void;
  reactions?: ReturnType<typeof useReactions>;
  onLeave: () => void;
  chat: ReturnType<typeof useChat>;
  onOpenDrawer?: () => void;
  userAvatar?: string;
  userName?: string;
  socialBadge?: number;
}

function isDigitalMode(mode: string): boolean {
  return mode === 'digital' || mode === 'duel';
}

export function GameBoardScreen({
  gameState,
  playerId,
  isHost,
  isSpectator,
  onAddCard,
  onRemoveCard,
  onStay,
  onBust,
  onNextPlayer,
  onUndo,
  onEndRound,
  onHit,
  onChooseTarget,
  onSelectSwapCards,
  onSelectStealTarget,
  onSelectDiscardTarget,
  onForfeitAction,
  onKickPlayer,
  reactions,
  onLeave,
  chat,
  onOpenDrawer,
  userAvatar,
  userName,
  socialBadge,
}: GameBoardScreenProps) {
  const play = useSound();
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];

  // Mute state (local)
  const [muted, setMutedState] = useState(getMuted);
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  // Chat panel state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatChannel, setChatChannel] = useState<ChatChannel>('room');
  const [chatInput, setChatInput] = useState('');

  // Soundbite sheet
  const [soundbiteSheetOpen, setSoundbiteSheetOpen] = useState(false);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLInputElement>(null);

  const chatMessages = chatChannel === 'room' ? chat.roomMessages : chat.spectatorMessages;
  const totalUnread = chat.unreadRoom + chat.unreadSpectator;

  useEffect(() => {
    if (chatOpen) {
      chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      chat.clearUnread(chatChannel);
    }
  }, [chatMessages.length, chatOpen, chatChannel, chat.clearUnread]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    chat.sendChat(chatChannel, chatInput);
    setChatInput('');
    chatInputRef.current?.focus();
  };

  // Round event toast
  const [eventToast, setEventToast] = useState<string | null>(null);
  const [eventToastType, setEventToastType] = useState<string | null>(null);
  const prevEventsLenRef = useRef(gameState.roundEvents.length);
  const eventToastTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const newLen = gameState.roundEvents.length;
    if (newLen > prevEventsLenRef.current && newLen > 0) {
      const latest = gameState.roundEvents[newLen - 1];
      const icon = eventIcon(latest.type);
      const text = formatRoundEvent(latest);
      setEventToast(`${icon} ${text}`);
      setEventToastType(latest.type);

      if (latest.type === 'freeze') play('freeze');

      const isDramatic = latest.type === 'second-chance-save' || latest.type === 'freeze';
      if (eventToastTimerRef.current) clearTimeout(eventToastTimerRef.current);
      eventToastTimerRef.current = setTimeout(() => { setEventToast(null); setEventToastType(null); }, isDramatic ? 2500 : 1500);
    }
    prevEventsLenRef.current = newLen;
  }, [gameState.roundEvents.length, play]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (eventToastTimerRef.current) clearTimeout(eventToastTimerRef.current);
    };
  }, []);

  // Host player selector
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  const effectiveSelectedId = isHost
    ? (selectedPlayerId ?? playerId)
    : playerId;

  const selectedPlayer = effectiveSelectedId
    ? gameState.players.find(p => p.id === effectiveSelectedId)
    : null;

  const displayPlayer = isHost ? (selectedPlayer ?? currentPlayer) : currentPlayer;
  const currentRound = displayPlayer?.currentRound;
  const cards = currentRound?.cards ?? [];

  const isMyTurn = currentPlayer?.id === playerId;
  const canInteract = isHost
    ? (displayPlayer?.currentRound?.status === 'active')
    : (isMyTurn && currentRound?.status === 'active');

  const scoreBreakdown = currentRound
    ? calculateScore(cards, currentRound.status === 'busted', gameState.variant)
    : null;

  // Reset selected player on round transitions
  const prevRoundRef = useRef(gameState.currentRound);
  useEffect(() => {
    if (gameState.currentRound !== prevRoundRef.current) {
      setSelectedPlayerId(null);
      prevRoundRef.current = gameState.currentRound;
    }
  }, [gameState.currentRound]);

  useEffect(() => {
    if (selectedPlayerId && !gameState.players.find(p => p.id === selectedPlayerId)) {
      setSelectedPlayerId(null);
    }
  }, [gameState.players, selectedPlayerId]);

  // Card flip sound
  const prevTurnPhaseRef = useRef(gameState.turnPhase);
  useEffect(() => {
    if (isDigitalMode(gameState.mode) && gameState.turnPhase === 'card-dealt' && prevTurnPhaseRef.current !== 'card-dealt') {
      play('cardFlip');
    }
    prevTurnPhaseRef.current = gameState.turnPhase;
  }, [gameState.turnPhase, gameState.mode, play]);

  // Your turn ping
  const prevPlayerIndexRef = useRef(gameState.currentPlayerIndex);
  useEffect(() => {
    if (!isDigitalMode(gameState.mode)) return;
    const changed = prevPlayerIndexRef.current !== gameState.currentPlayerIndex;
    prevPlayerIndexRef.current = gameState.currentPlayerIndex;
    if (changed && currentPlayer?.id === playerId && gameState.turnPhase === 'awaiting-decision') {
      play('yourTurn');
    }
  }, [gameState.currentPlayerIndex, gameState.turnPhase, gameState.mode, currentPlayer?.id, playerId, play]);

  // Status sound triggers
  const prevStatusRef = useRef(currentRound?.status);
  const prevScoreRef = useRef(scoreBreakdown?.totalScore ?? 0);
  const scoreDisplayRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    const newStatus = currentRound?.status;
    if (prevStatus !== newStatus) {
      if (newStatus === 'busted') play('bust');
      else if (newStatus === 'frozen') play('freeze');
      else if (newStatus === 'stayed') play('stay');
    }
    prevStatusRef.current = newStatus;
  }, [currentRound?.status, play]);

  // Score pulse + Flip 7 sound
  useEffect(() => {
    const newScore = scoreBreakdown?.totalScore ?? 0;
    if (newScore !== prevScoreRef.current && newScore > 0) {
      scoreDisplayRef.current?.classList.remove('score-pulse');
      void scoreDisplayRef.current?.offsetWidth;
      scoreDisplayRef.current?.classList.add('score-pulse');
      if (scoreBreakdown?.flip7Bonus && scoreBreakdown.flip7Bonus > 0) {
        play('flip7');
      }
    }
    prevScoreRef.current = newScore;
  }, [scoreBreakdown?.totalScore, scoreBreakdown?.flip7Bonus, play]);

  const handleCardToggle = (card: CardType) => {
    if (!displayPlayer || !canInteract) return;
    const existingIndex = cards.findIndex(
      (c) => c.type === card.type && c.value === card.value,
    );
    if (existingIndex !== -1) {
      onRemoveCard(displayPlayer.id, card);
    } else {
      onAddCard(displayPlayer.id, card);
    }
    play('cardTap');
  };

  const handleStay = () => { if (displayPlayer) onStay(displayPlayer.id); };
  const handleBust = () => { if (displayPlayer) onBust(displayPlayer.id); };
  const handleNextPlayer = () => { onNextPlayer(); play('nextPlayer'); };
  const handleUndo = () => { onUndo(); play('undo'); };

  const handlePlayerClick = (clickedPlayerId: string) => {
    if (!isHost) return;
    setSelectedPlayerId(clickedPlayerId);
  };

  const statusClass =
    currentRound?.status === 'busted' ? 'bust-shake badge-pop' :
    currentRound?.status === 'frozen' ? 'freeze-glow badge-pop' :
    currentRound?.status === 'stayed' ? 'badge-pop' : '';

  // Whether to show Hit/Stay inside the main area (digital mode, your turn)
  const showDigitalActions = isDigitalMode(gameState.mode)
    && isMyTurn
    && !currentPlayer?.isBot
    && currentPlayer?.currentRound?.status === 'active'
    && gameState.turnPhase === 'awaiting-decision';

  return (
    <div className={`game-container game-board-container${currentRound?.status === 'frozen' ? ' game-board--frozen' : ''}${gameState.mode === 'duel' ? ' game-board--duel' : ''}`}>
      {/* ── Header bar ── */}
      <div className="board__header">
        <button className="board__header-btn board__header-btn--back" onClick={onLeave} aria-label="Leave game">
          {'\u2190'}
        </button>
        <div className="board__header-info">
          {gameState.duelData ? (
            <>
              <div className="duel-series">
                {[0, 1, 2].map(i => {
                  const [p1w, p2w] = gameState.duelData!.roundWins;
                  const myIdx = gameState.players[0]?.id === playerId ? 0 : 1;
                  const myWins = myIdx === 0 ? p1w : p2w;
                  const oppWins = myIdx === 0 ? p2w : p1w;
                  const won = i < myWins;
                  const lost = i < oppWins;
                  return <div key={i} className={`duel-series__dot${won ? ' duel-series__dot--won' : lost ? ' duel-series__dot--lost' : ''}`} />;
                })}
                <span className="duel-series__label">Best of {gameState.duelData.bestOf}</span>
              </div>
            </>
          ) : (
            <>
              <span className="board__header-chip board__header-chip--round">R{gameState.currentRound}</span>
              <span className="board__header-chip">Target {gameState.targetScore}</span>
            </>
          )}
          {/* Hide deck count and extras in duel — keep it clean */}
          {!gameState.duelData && (
            <span className="board__header-chip">{gameState.deckRemaining} left</span>
          )}
          {gameState.variant && gameState.variant !== 'standard' && (
            <span className="board__header-chip board__header-chip--variant">
              {gameState.variant === 'vengeance-brutal' ? 'Brutal' : 'Vengeance'}
            </span>
          )}
          {!gameState.duelData && gameState.pot != null && gameState.pot > 0 && (
            <span className="board__header-chip coin-badge" style={{ fontSize: '0.6rem' }}>
              Pot: {gameState.pot}
            </span>
          )}
          {gameState.spectators.length > 0 && (
            <span
              className="board__header-chip board__header-chip--spectators"
              title={gameState.spectators.map(s => s.name).join(', ')}
            >
              {'\uD83D\uDC41'} {gameState.spectators.length <= 2
                ? gameState.spectators.map(s => s.name).join(', ')
                : `${gameState.spectators[0].name} +${gameState.spectators.length - 1}`}
            </span>
          )}
        </div>
        <div className="board__header-actions">
          {onOpenDrawer && userName && (
            <button
              className="board__header-btn board__header-btn--profile"
              onClick={onOpenDrawer}
              aria-label="Open profile"
            >
              {userAvatar || userName.charAt(0).toUpperCase()}
              {(socialBadge ?? 0) > 0 && (
                <span className="board__header-badge">{(socialBadge ?? 0) > 9 ? '9+' : socialBadge}</span>
              )}
            </button>
          )}
          <button
            className="board__header-btn"
            onClick={() => { setChatOpen(!chatOpen); if (!chatOpen) chat.clearUnread(chatChannel); }}
            aria-label={chatOpen ? 'Close chat' : 'Open chat'}
          >
            {'\uD83D\uDCAC'}
            {!chatOpen && totalUnread > 0 && (
              <span className="board__header-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
          </button>
          <button
            className="board__header-btn"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
          >
            {muted ? '\uD83D\uDD07' : '\uD83D\uDD0A'}
          </button>
        </div>
      </div>

      {/* ── Desktop layout wrapper ── */}
      <div className="board__layout">
        {/* Player sidebar (desktop) / strip (mobile) */}
        <div className="board__players">
          <PlayerStrip
            players={gameState.players}
            currentPlayerIndex={gameState.currentPlayerIndex}
            myPlayerId={playerId}
            selectedPlayerId={isHost ? effectiveSelectedId : undefined}
            onPlayerClick={isHost ? handlePlayerClick : undefined}
            onKickPlayer={isHost ? onKickPlayer : undefined}
          />
        </div>

        {/* Main board column */}
        <div className="board__content">
          {/* Event toast */}
          {eventToast && (
            <div
              key={eventToast}
              className={`event-toast--board badge-pop${eventToastType === 'second-chance-save' ? ' event-toast--second-chance' : eventToastType === 'freeze' ? ' event-toast--freeze' : ''}`}
            >
              {eventToast}
            </div>
          )}

          {/* Main board area */}
          <div className="board__main">
            {isDigitalMode(gameState.mode) ? (
              <div key={`${gameState.currentPlayerIndex}`} className="turn-entrance">
                <DigitalModeControls
                  gameState={gameState}
                  playerId={isSpectator ? null : playerId}
                  currentPlayer={currentPlayer}
                  onChooseTarget={onChooseTarget}
                  onSelectSwapCards={onSelectSwapCards}
                  onSelectStealTarget={onSelectStealTarget}
                  onSelectDiscardTarget={onSelectDiscardTarget}
                  onForfeitAction={onForfeitAction}
                />
                {/* Hit/Stay inside turn area */}
                {showDigitalActions && (
                  <div className="board__turn-actions">
                    <button className="btn btn-primary" onClick={() => onHit(currentPlayer.id)}>
                      HIT
                    </button>
                    <button className="btn btn-success" onClick={handleStay}>
                      STAY
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Score tracker mode */}
                <div key={`st-${effectiveSelectedId}-${gameState.currentPlayerIndex}`} className="ornate-border turn-entrance score-tracker-panel" aria-live="polite">
                  <p className="label">
                    {isHost && displayPlayer?.id !== currentPlayer?.id
                      ? 'Managing Cards For'
                      : 'Current Player'}
                  </p>
                  <p className="score-tracker__name">
                    {displayPlayer?.name ?? '\u2014'}
                  </p>

                  {currentRound && (
                    <div className="score-tracker__status">
                      {currentRound.status === 'busted' && (
                        <p className={`${statusClass} score-tracker__status-text score-tracker__status-text--bust`}>
                          BUST!{currentRound.bustCard?.type === 'number' ? ` (drew ${currentRound.bustCard.value})` : ''}
                        </p>
                      )}
                      {currentRound.status === 'stayed' && (
                        <p className={`${statusClass} score-tracker__status-text score-tracker__status-text--stayed`}>SETTLED</p>
                      )}
                      {currentRound.status === 'frozen' && (
                        <p className={`${statusClass} score-tracker__status-text score-tracker__status-text--frozen`}>FROZEN</p>
                      )}

                      {scoreBreakdown && currentRound.status !== 'busted' && (
                        <div className="score-tracker__score-detail">
                          <span
                            ref={scoreDisplayRef}
                            className={`score-tracker__score-value${scoreBreakdown.flip7Bonus > 0 ? ' flip7-celebrate' : ''}`}
                          >
                            {scoreBreakdown.totalScore}
                          </span>
                          <div className="score-tracker__score-breakdown">
                            {scoreBreakdown.baseSum > 0 && <span>Base: {scoreBreakdown.baseSum}</span>}
                            {scoreBreakdown.hasMultiplier && <span> x2 = {scoreBreakdown.multipliedBase}</span>}
                            {scoreBreakdown.positiveModifierSum > 0 && <span> +{scoreBreakdown.positiveModifierSum} mods</span>}
                            {scoreBreakdown.negativeModifierSum < 0 && <span style={{ color: 'var(--color-bust)' }}> {scoreBreakdown.negativeModifierSum}</span>}
                            {scoreBreakdown.hasDiv2 && <span> ÷2</span>}
                            {scoreBreakdown.flip7Bonus > 0 && (
                              <span style={{ color: 'var(--gold)', fontWeight: 700 }}> +15 FLIP 7!</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Score tracker actions inside the panel */}
                  {(canInteract || isHost) && (
                    <div className={`board__turn-actions${isHost ? ' board__turn-actions--host' : ''}`}>
                      {canInteract && (
                        <>
                          <button className="btn btn-success" onClick={handleStay}>Settle</button>
                          <button className="btn btn-danger" onClick={handleBust}>Bust</button>
                        </>
                      )}
                      {isHost && (
                        <>
                          <button className="btn btn-secondary" onClick={handleNextPlayer}>Next</button>
                          <button className="btn btn-secondary" onClick={handleUndo}>Undo</button>
                          <button className="btn btn-secondary btn--end-round" onClick={onEndRound}>End Round</button>
                        </>
                      )}
                    </div>
                  )}

                  {!canInteract && !isHost && !isMyTurn && (
                    <p className="board__waiting-text">
                      Waiting for {currentPlayer?.name}&rsquo;s turn...
                    </p>
                  )}
                </div>

                <CardGrid
                  selectedCards={cards}
                  onCardToggle={handleCardToggle}
                  disabled={!canInteract}
                />
              </>
            )}
            {/* Reaction toasts */}
            {reactions && reactions.toasts.length > 0 && (
              <div className="board__toasts">
                <ReactionToasts toasts={reactions.toasts} />
              </div>
            )}
          </div>

          {/* Compact reaction bar — always visible */}
          {reactions && (
            <div className="board__reactions">
              <ReactionBar
                onSendReaction={reactions.sendReaction}
                onStartRecording={reactions.startRecording}
                onStopRecording={reactions.stopRecording}
                isRecording={reactions.isRecording}
                cooldown={reactions.cooldown}
                playingSoundbite={reactions.playingSoundbite}
                onOpenSoundbiteSheet={() => setSoundbiteSheetOpen(true)}
              />
            </div>
          )}
          {reactions && (
            <SoundbiteSheet
              open={soundbiteSheetOpen}
              onClose={() => setSoundbiteSheetOpen(false)}
              onToggleSoundbite={reactions.toggleSoundbite}
              soundbiteCooldown={reactions.soundbiteCooldown}
              playingSoundbite={reactions.playingSoundbite}
            />
          )}
        </div>

        {/* Chat sidebar (desktop) — rendered inside layout */}
        {chatOpen && (
          <div className="board__chat">
            <div className="board__chat-header">
              <button
                onClick={() => { setChatChannel('room'); chat.clearUnread('room'); }}
                className={`board__chat-tab${chatChannel === 'room' ? ' board__chat-tab--active' : ''}`}
              >
                Room
                {chat.unreadRoom > 0 && chatChannel !== 'room' && (
                  <span className="board__chat-unread">({chat.unreadRoom})</span>
                )}
              </button>
              {isSpectator && (
                <button
                  onClick={() => { setChatChannel('spectator'); chat.clearUnread('spectator'); }}
                  className={`board__chat-tab${chatChannel === 'spectator' ? ' board__chat-tab--active' : ''}`}
                >
                  Spectators
                  {chat.unreadSpectator > 0 && chatChannel !== 'spectator' && (
                    <span className="board__chat-unread">({chat.unreadSpectator})</span>
                  )}
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button className="board__chat-close" onClick={() => setChatOpen(false)} aria-label="Close chat">
                {'\u2715'}
              </button>
            </div>
            <div className="board__chat-messages">
              {chatMessages.length === 0 && (
                <div className="board__chat-empty">No messages yet</div>
              )}
              {chatMessages.map(msg => (
                <div key={msg.id} className="chat-msg">
                  {msg.senderRole === 'system' ? (
                    <span className="chat-msg__system">{msg.content}</span>
                  ) : (
                    <>
                      <span className={`chat-msg__sender${msg.senderRole === 'spectator' ? ' chat-msg__sender--spectator' : ''}`}>
                        {msg.senderName}
                        {msg.senderRole === 'spectator' && (
                          <span className="chat-msg__role">(spectator)</span>
                        )}
                      </span>
                      <span className="chat-msg__content">{msg.content}</span>
                    </>
                  )}
                </div>
              ))}
              <div ref={chatMessagesEndRef} />
            </div>
            <div className="board__chat-input">
              <input
                ref={chatInputRef}
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value.slice(0, CHAT_MAX_LENGTH))}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                placeholder={chatChannel === 'spectator' ? 'Spectators only...' : 'Message...'}
                maxLength={CHAT_MAX_LENGTH}
              />
              <button onClick={handleChatSend} disabled={!chatInput.trim()} className="btn btn-primary board__chat-send">
                Send
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bust overlay */}
      {isDigitalMode(gameState.mode) && gameState.turnPhase === 'bust-result' && currentPlayer?.currentRound?.bustCard && (
        <CardReveal
          key={`bust-${gameState.currentPlayerIndex}-${gameState.currentRound}`}
          card={currentPlayer.currentRound.bustCard}
          isBust={true}
          playerName={currentPlayer.name}
          onComplete={() => {/* bust overlay auto-dismisses */}}
        />
      )}
    </div>
  );
}
